import { api, ApiError } from '../api/client';
import type { IngestFinalizeResponse, ListDocumentsResponse, RoomDocument } from '../api/types';
import { relativePathOf } from './drop-files';

/**
 * BULK UPLOAD, client side (rebuild 2026-10-12, ADR-0061) — shared by the
 * admin's upload panel and the seller's submit page, so a 2,500-file folder
 * goes in within a few minutes instead of failing past the hundredth file.
 *
 *  - Each file is sent as its RAW BYTES ({@link sendRoomUpload}), not base64.
 *  - {@link UPLOAD_CONCURRENCY} files are in flight at once — what the server
 *    works on for one person at a time (`UPLOAD_SLOTS_PER_ACTOR`), so a file
 *    waits here, not in the server's queue.
 *  - "Busy" (503 `upload_busy`: the server's upload slots stayed full for a
 *    minute) only delays a file — 1 s, 2 s, 4 s, then every 8 s — and costs
 *    none of its attempts while the queue is still getting files in.
 *  - A proxy's mangled "busy": the server answers 503 `upload_busy` on the
 *    connection itself, and a proxy in front of it (the Pages worker, Fly) may
 *    pass that on as a 502 / 520 or a dropped connection instead. Such a
 *    failure within {@link UPLOAD_FAST_FAILURE_MS} of sending is treated like
 *    "busy" — free — up to {@link UPLOAD_FREE_FAST_RETRIES} times per file (so a
 *    proxy that fails everything at once still ends); its outcome still counts
 *    as unknown (see "skipped" below).
 *  - A free trial's cap (403 `trial_limit_reached`) answered after an attempt
 *    whose outcome was unknown may mean THAT attempt landed (the cap is checked
 *    before the duplicate check): `confirmLanded` looks for the file in the
 *    room — same name, folder and size, created since the queue started — before
 *    it is shown as failed ({@link createLandedUploadFinder}: one listing read
 *    per pass, not per file).
 *  - Any other passing failure — no connection, 408, 429, a 5xx — is sent again
 *    on its own, up to {@link UPLOAD_MAX_ATTEMPTS} times, after the wait the
 *    server asked for (Retry-After) or an exponential backoff. A 429 holds back
 *    the WHOLE queue for that long. Every file stands alone: a failure costs
 *    that file, never the batch.
 *  - The first 401 (the session ended) stops the whole queue: nothing more is
 *    sent, every waiting file fails at once, and `onSessionExpired` fires once.
 *  - Files can be added at any time; they join the one queue.
 *
 * Re-sending a file is safe: the server skips bytes the room already holds
 * (ADR-0028). A file whose earlier attempt MAY have landed (no answer, or a
 * gateway error) and comes back "skipped" is this upload, not a duplicate the
 * person picked — `onSuccess` says so (`mayHaveLandedEarlier`).
 */

/** Files in flight at once — the server's per-person upload slots (ADR-0061). */
export const UPLOAD_CONCURRENCY = 2;
/** Attempts per file before it is shown as failed (it can still be retried by
 * hand). "Busy" answers do not count while the queue is moving. */
export const UPLOAD_MAX_ATTEMPTS = 6;
/** With no file in for this long, "busy" answers start to count as attempts (so a stuck queue ends). */
export const UPLOAD_STALL_MS = 5 * 60_000;
/** A gateway error or dropped connection this soon after sending is most likely a proxied "busy". */
export const UPLOAD_FAST_FAILURE_MS = 2_000;
/** How many such fast failures one file may have for free. */
export const UPLOAD_FREE_FAST_RETRIES = 3;

/** C0/C1 controls, DEL, zero-width characters, bidi marks/overrides/isolates, BOM
 * — the characters the server strips from a folder path (ingestion/folder-path.ts). */
const INVISIBLE_PATH_CHARS =
  // eslint-disable-next-line no-control-regex -- stripping control characters is the point
  /[\u0000-\u001f\u007f-\u009f​-‏‪-‮⁦-⁩﻿]/g;

/**
 * The relative path an upload sends: only the part of the folder the SERVER
 * keeps (`normalizeFolderPath`, src/modules/ingestion/folder-path.ts — the 8
 * folders nearest the file, each at most 120 characters, at most 500 in all,
 * empty and dot folders dropped), then the file's own name. The rest would be
 * thrown away on arrival, and in a header it costs: percent-encoded, one CJK
 * character is nine bytes, so a deep non-Latin path could pass Node's 16 KB
 * header limit and the upload would fail with 431 (ADR-0061). Undefined when no
 * folder is left (a loose file). Pure.
 */
export function uploadRelativePath(path: string | undefined): string | undefined {
  if (!path) return undefined;
  const segments = path
    .normalize('NFC')
    .replace(INVISIBLE_PATH_CHARS, '')
    .replace(/\\/g, '/')
    .split('/');
  const name = segments.pop() ?? '';
  let folders = segments
    .map((s) => s.trim())
    .filter((s) => s !== '' && !/^\.+$/.test(s))
    .map((s) => s.slice(0, 120).trim())
    .filter((s) => s !== '')
    .slice(-8);
  while (folders.length > 1 && folders.join('/').length > 500) folders = folders.slice(1);
  if (folders.length === 0 || folders.join('/').length > 500) return undefined;
  return `${folders.join('/')}/${name}`;
}

/** Upload one file into a room as raw bytes — name, declared type and folder
 * path percent-encoded in `x-upload-*` headers (headers carry only Latin-1). */
export function sendRoomUpload(roomId: string, file: File): Promise<IngestFinalizeResponse> {
  const relativePath = uploadRelativePath(relativePathOf(file));
  return api.postBytes<IngestFinalizeResponse>(`/rooms/${roomId}/ingest/finalize`, file, {
    'X-Upload-Filename': encodeURIComponent(file.name),
    ...(file.type ? { 'X-Upload-Content-Type': encodeURIComponent(file.type) } : {}),
    // The folder it came from is a classification clue (ADR-0018).
    ...(relativePath ? { 'X-Upload-Relative-Path': encodeURIComponent(relativePath) } : {}),
  });
}

/** How far the server's clock may be behind this one when "created since the queue started" is judged. */
export const LANDED_CLOCK_SKEW_MS = 5 * 60_000;

/**
 * The listed document that is `file`, uploaded during this pass of the queue:
 * the same name, the same size, the same folder (when the listing says — only
 * the room admin is told the folder), created no earlier than `passStartedAt`
 * (less {@link LANDED_CLOCK_SKEW_MS}). The listing carries no content hash, so
 * this is the match; a document with all of that in common is, for the panel,
 * this upload. Pure.
 */
export function landedUploadIn(
  documents: readonly RoomDocument[],
  file: File,
  passStartedAt: number,
): RoomDocument | null {
  const path = uploadRelativePath(relativePathOf(file));
  const folder = path ? path.slice(0, path.lastIndexOf('/')) : null;
  return (
    documents.find(
      (d) =>
        d.name === file.name &&
        d.sizeBytes === file.size &&
        (typeof d.folderPath !== 'string' || d.folderPath === folder) &&
        Date.parse(d.createdAt) >= passStartedAt - LANDED_CLOCK_SKEW_MS,
    ) ?? null
  );
}

/**
 * A `confirmLanded` for one room: did a file whose answer was lost land after
 * all ({@link landedUploadIn})? The room's listing is read once and reused for
 * every file that asks in the same pass — re-read only for a file whose
 * unknown attempt ended after that read (it could have landed since). Null when
 * it did not land, or the listing cannot be read.
 */
export function createLandedUploadFinder(
  roomId: () => string,
): (file: File, info: LandedCheck) => Promise<IngestFinalizeResponse | null> {
  let listing: { readAt: number; documents: Promise<RoomDocument[]> } | null = null;
  return async (file, info) => {
    if (!listing || listing.readAt < info.outcomeUnknownAt) {
      listing = {
        readAt: Date.now(),
        documents: api
          .get<ListDocumentsResponse>(`/rooms/${roomId()}/documents`)
          .then((r) => r.documents),
      };
    }
    const current = listing;
    try {
      const match = landedUploadIn(await current.documents, file, info.passStartedAt);
      return match ? { documentId: match.id, skipped: false } : null;
    } catch {
      if (listing === current) listing = null;
      return null;
    }
  };
}

/** True for a failure worth sending the file again for: no answer at all, a
 * timeout, "too many requests", or a server-side error. A refusal (400 / 401 /
 * 403 / 404 / 409 / 413) would only be refused again. */
export function isRetryableUploadError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500;
}

/** The server's upload slots stayed full (503 `upload_busy`): the file was never read. */
export function isUploadBusy(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 503 &&
    (error.problem as { code?: unknown } | undefined)?.code === 'upload_busy'
  );
}

/** No answer at all, or a gateway failure (502, or Cloudflare's 520) — what a
 * proxy may turn the server's on-the-socket "busy" answer into. */
function gatewayOrNoAnswer(error: unknown): boolean {
  return error instanceof ApiError && [0, 502, 520].includes(error.status);
}

/** A free trial at its document cap (403 `trial_limit_reached`). */
export function isTrialLimitReached(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 403 &&
    (error.problem as { code?: unknown } | undefined)?.code === 'trial_limit_reached'
  );
}

/** The attempt's outcome is unknown — it may have been stored before its answer
 * was lost: no answer at all, or a server-side error other than "busy" (a
 * gateway's 502 / 504 says nothing about the app behind it). */
function outcomeUnknown(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status === 0 || (error.status >= 500 && !isUploadBusy(error));
}

/** How long to wait before attempt `attempt + 1`: the server's hint, else 1 s,
 * 2 s, 4 s … capped at 30 s — plus up to half a second of jitter so a queue's
 * worth of retries does not land at the same instant. */
export function uploadRetryDelayMs(
  error: unknown,
  attempt: number,
  random: () => number = Math.random,
): number {
  const hinted = error instanceof ApiError ? error.retryAfterSeconds : undefined;
  const base =
    hinted !== undefined ? hinted * 1000 : Math.min(30_000, 1000 * 2 ** Math.max(0, attempt - 1));
  return base + Math.floor(random() * 500);
}

/** How long to wait after the `streak`-th "busy" in a row: 1 s, 2 s, 4 s, then 8 s (+ jitter). */
export function uploadBusyDelayMs(streak: number, random: () => number = Math.random): number {
  return Math.min(8_000, 1000 * 2 ** Math.max(0, streak - 1)) + Math.floor(random() * 500);
}

export interface UploadJob {
  id: number;
  file: File;
}

export interface UploadQueueHandlers<R> {
  /** Sends one file (normally {@link sendRoomUpload}). */
  send: (file: File) => Promise<R>;
  /** The file's request is going out (first attempt). */
  onStart?: (id: number) => void;
  /** It failed for a passing reason and will be sent again after `delayMs`. */
  onRetrying?: (id: number, attempt: number, delayMs: number) => void;
  /** `mayHaveLandedEarlier`: an earlier attempt's outcome was unknown, so a
   * "skipped" answer is most likely that attempt, not a duplicate. */
  onSuccess: (
    id: number,
    result: R,
    info: { attempts: number; mayHaveLandedEarlier: boolean },
  ) => void;
  onFailure: (id: number, error: unknown) => void;
  /**
   * After a free trial's cap refused a file whose earlier attempt's outcome was
   * unknown: did that attempt land? Resolves the result to report as a success
   * (with `mayHaveLandedEarlier`), or null — the file failed. Omitted: it failed.
   */
  confirmLanded?: (file: File, info: LandedCheck) => Promise<R | null>;
  /** After success or failure — once per added job. */
  onSettled?: (id: number) => void;
  /** Nothing waiting and nothing in flight any more. */
  onIdle?: () => void;
  /** The session ended (the first 401): the queue has stopped. Once per stop. */
  onSessionExpired?: (error: unknown) => void;
}

/** What `confirmLanded` is told about the file's pass and its lost answer. */
export interface LandedCheck {
  /** When the queue started this pass (it was idle, then files were added). */
  passStartedAt: number;
  /** When the file's last attempt with an unknown outcome ended. */
  outcomeUnknownAt: number;
}

export interface UploadQueueOptions {
  concurrency?: number;
  maxAttempts?: number;
  /** Test seam. */
  random?: () => number;
}

export interface UploadQueue {
  /** Queue files (they start as soon as there is room). After the queue stopped
   * on an ended session, adding files starts it again. */
  add: (jobs: UploadJob[]) => void;
  /** How many files are waiting or in flight. */
  pending: () => number;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function createUploadQueue<R>(
  handlers: UploadQueueHandlers<R>,
  options: UploadQueueOptions = {},
): UploadQueue {
  const concurrency = options.concurrency ?? UPLOAD_CONCURRENCY;
  const maxAttempts = options.maxAttempts ?? UPLOAD_MAX_ATTEMPTS;
  const random = options.random ?? Math.random;
  const waiting: UploadJob[] = [];
  let active = 0;
  /** A 429 holds every new request back until then (epoch ms). */
  let pausedUntil = 0;
  let wakeTimer: ReturnType<typeof setTimeout> | null = null;
  /** When a file last got in (or the queue last started): "busy" is free until it stalls. */
  let lastProgressAt = Date.now();
  /** When this pass started (the queue was idle, then files were added). */
  let passStartedAt = Date.now();
  /** The 401 that stopped the queue, or null while it runs. */
  let stopped: unknown = null;

  function stop(error: unknown): void {
    if (stopped !== null) return;
    stopped = error;
    handlers.onSessionExpired?.(error);
    for (const job of waiting.splice(0)) {
      handlers.onFailure(job.id, error);
      handlers.onSettled?.(job.id);
    }
  }

  function pump(): void {
    const pause = pausedUntil - Date.now();
    if (pause > 0) {
      wakeTimer ??= setTimeout(() => {
        wakeTimer = null;
        pump();
      }, pause);
      return;
    }
    while (waiting.length > 0 && active < concurrency) {
      const next = waiting.shift()!;
      active += 1;
      void run(next);
    }
  }

  async function run(job: UploadJob): Promise<void> {
    handlers.onStart?.(job.id);
    let attempt = 1;
    let busyStreak = 0;
    let freeFastRetries = UPLOAD_FREE_FAST_RETRIES;
    let mayHaveLandedEarlier = false;
    /** When the last attempt whose outcome is unknown ended. */
    let outcomeUnknownAt = 0;
    for (;;) {
      const pause = pausedUntil - Date.now();
      if (pause > 0) await sleep(pause);
      if (stopped !== null) {
        handlers.onFailure(job.id, stopped);
        break;
      }
      const sentAt = Date.now();
      try {
        const result = await handlers.send(job.file);
        lastProgressAt = Date.now();
        handlers.onSuccess(job.id, result, { attempts: attempt, mayHaveLandedEarlier });
        break;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          handlers.onFailure(job.id, error);
          stop(error);
          break;
        }
        const moving = Date.now() - lastProgressAt < UPLOAD_STALL_MS;
        const proxiedBusy =
          gatewayOrNoAnswer(error) &&
          Date.now() - sentAt < UPLOAD_FAST_FAILURE_MS &&
          freeFastRetries > 0;
        if (moving && (isUploadBusy(error) || proxiedBusy)) {
          // Never read by the server (or, a proxy's "busy", most likely not), and
          // the queue is moving: wait, free of charge.
          if (proxiedBusy) {
            freeFastRetries -= 1;
            mayHaveLandedEarlier = true; // not certain it was "busy"
            outcomeUnknownAt = Date.now();
          }
          busyStreak += 1;
          const delay = uploadBusyDelayMs(busyStreak, random);
          handlers.onRetrying?.(job.id, attempt, delay);
          await sleep(delay);
          continue;
        }
        if (isTrialLimitReached(error) && mayHaveLandedEarlier && handlers.confirmLanded) {
          // The cap is checked before the duplicate check: an earlier attempt
          // whose answer was lost may be the very document that filled it.
          const landed = await handlers
            .confirmLanded(job.file, { passStartedAt, outcomeUnknownAt })
            .catch(() => null);
          if (landed !== null) {
            lastProgressAt = Date.now();
            handlers.onSuccess(job.id, landed, { attempts: attempt, mayHaveLandedEarlier });
          } else {
            handlers.onFailure(job.id, error);
          }
          break;
        }
        if (attempt >= maxAttempts || !isRetryableUploadError(error)) {
          handlers.onFailure(job.id, error);
          break;
        }
        if (outcomeUnknown(error)) {
          mayHaveLandedEarlier = true;
          outcomeUnknownAt = Date.now();
        }
        const delay = uploadRetryDelayMs(error, attempt, random);
        if (error instanceof ApiError && error.status === 429) {
          pausedUntil = Math.max(pausedUntil, Date.now() + delay);
        }
        handlers.onRetrying?.(job.id, attempt, delay);
        attempt += 1;
        busyStreak = 0;
        await sleep(delay);
      }
    }
    active -= 1;
    handlers.onSettled?.(job.id);
    if (active === 0 && waiting.length === 0) handlers.onIdle?.();
    else pump();
  }

  return {
    add(jobs) {
      stopped = null; // files added after an ended session: signed in again, start over
      if (active === 0 && waiting.length === 0) {
        lastProgressAt = Date.now();
        passStartedAt = Date.now();
      }
      waiting.push(...jobs);
      pump();
    },
    pending: () => waiting.length + active,
  };
}

/** An upload request's fields as the server reads them, whichever body shape it
 * used (raw bytes + `x-upload-*` headers, or base64 JSON) — for the offline
 * mocks and the tests, which see the request before anything is sent. */
export interface UploadRequestFields {
  filename: string;
  contentType?: string;
  relativePath?: string;
  /** No bytes at all (the server refuses an empty file). */
  empty: boolean;
}

export function uploadRequestFields(init: RequestInit | undefined): UploadRequestFields {
  const body = init?.body;
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    const headers = new Headers(init?.headers);
    const header = (name: string) => {
      const value = headers.get(name);
      return value === null ? undefined : decodeURIComponent(value);
    };
    const contentType = header('x-upload-content-type');
    const relativePath = header('x-upload-relative-path');
    return {
      filename: header('x-upload-filename') ?? '',
      ...(contentType !== undefined ? { contentType } : {}),
      ...(relativePath !== undefined ? { relativePath } : {}),
      empty: body.size === 0,
    };
  }
  const json = (body ? JSON.parse(String(body)) : {}) as {
    filename?: string;
    contentType?: string;
    relativePath?: string;
    contentBase64?: string;
  };
  return {
    filename: json.filename ?? '',
    ...(json.contentType !== undefined ? { contentType: json.contentType } : {}),
    ...(json.relativePath !== undefined ? { relativePath: json.relativePath } : {}),
    empty: !json.contentBase64,
  };
}
